import { ContainerTypes, DrawResultContextStateType, DrawResultContextValueType, DrawResultProviderProps } from '@src/lib/types/draw-result-page';
import { useInputHelper } from '@src/utils/inputs-helper';
import _ from 'lodash';
import _debounce from 'lodash/debounce';
import React, { createContext, useCallback, useContext } from 'react';
import { useServices } from './hooks';

const initialState: DrawResultContextStateType = {
  "description": "",
  "image": "",
  page: 0,
  order: 'desc',
  orderBy: 'drawAt',
  filterName: '',
  rowsPerPage: 10,
  fromdate: null,
  todate: null,
  search: '',
  debouncedSearch: '',
} as DrawResultContextStateType

export const DrawResultContext = createContext<DrawResultContextValueType>({} as DrawResultContextValueType);

const DrawResultProvider = ({ children }: DrawResultProviderProps) => {
  const { data, onPublish } = useServices();
  const [error, setError ] = React.useState({});
  const [success, setSuccess ] = React.useState(false);
  const [ viewWinners, setViewWinners ] = React.useState<any>({});
  const { state, onSetInitial, onDispatch } = useInputHelper(initialState);
  const [ container, setContainer ] = React.useState<ContainerTypes>("" as ContainerTypes);

  const onSubmit = React.useCallback(() => {
    const Err: any = {};
    if(!state.image){
      Err["image"] = true
    }
    if(!state.description){
      Err["description"] = true
    }

    setError(Err);

    if(_.isEmpty(Err)){
      onPublish.mutate(state, {
        onSuccess: () => {
          setSuccess(true)
        }
      });
    }
  }, [state, onPublish]);

  const handleRequestSort = (property: string) => {
    const isAsc = state.orderBy === property && state.order === 'asc';
    onDispatch('order')(isAsc ? 'desc' : 'asc');
    onDispatch('orderBy')(property);
  };

  const debouncedSearchFn = (value: string) => {
    // onDispatch('page')(0);
    onDispatch('debouncedSearch')(value);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearch = useCallback(_debounce(debouncedSearchFn, 1000), []);

  const handleSearch = (value: string) => {
    onDispatch('search')(value);
    handleDebouncedSearch(value);
  }

  const isDirty = !_.isEqual(
    { image: state.image, description: state.description },
    { image: data?.image ?? "", description: data?.description ?? "" }
  );

  const value = {
    state,
    error,
    onDispatch,
    onSubmit,
    isLoading: onPublish.isPending,
    isDirty,
    container, setContainer,
    success, setSuccess,
    viewWinners, setViewWinners,
    handleSearch,
    handleRequestSort,
  };

  React.useEffect(() => {
    if(!_.isEmpty(error)){
      setError({});
    }
  },[state]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if(!_.isEmpty(data)){
      onSetInitial({...data});
    }
  },[data, onSetInitial]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DrawResultContext.Provider value={value}>
      {children}
    </DrawResultContext.Provider>
  );
};

export const useDrawResultContext = () => {
  return useContext(DrawResultContext);
}

export default DrawResultProvider;
